import { useEffect, useState } from 'react'
import { useLiveQuery } from 'dexie-react-hooks'
import type {
  CashMovement,
  CashSession,
  Category,
  Customer,
  Item,
  Page,
  PartDepense,
  Pas,
  Supplier,
  SupplierQuery,
  RendezVous,
  SortiesTranche,
  Tax,
  Transaction,
} from '@iobiz/shared'
import {
  historiqueSessions,
  mouvementsDeSession,
  sessionOuverte,
} from './repositories/caisse.local.js'
import { dbFor, type ConflictRow } from './db.js'
import {
  citiesLocal,
  etiquettesLocales,
  getLocal,
  joursDeCreationClients,
  searchLocal,
  type RequeteClients,
} from './repositories/customers.local.js'
import {
  chiffreAffaires,
  idsArticlesAbsents,
  serieVentes,
  tableauDeBord,
  type CoutsArticles,
  listerVentes,
  referencesParId,
  type RequeteVentes,
  lireVente,
  totauxDuJour,
  ventesDuClient,
} from './repositories/sales.local.js'
import {
  getItemLocal,
  getSupplierLocal,
  searchItemsLocal,
  searchSuppliersLocal,
  toCategory,
  type RequeteArticles,
} from './repositories/catalog.local.js'
import { rendezVousDuClient, rendezVousParVente } from './repositories/agenda.local.js'
import { currentStatus, subscribe, type SyncStatus } from './sync/engine.js'
import { api } from '../api/client.js'

/**
 * Accès réactif à la réplique locale.
 *
 * `useLiveQuery` re-rend le composant dès que la donnée locale change, qu'elle
 * vienne d'une saisie ou d'une synchronisation entrante. Les pages n'ont donc
 * ni à recharger ni à savoir qu'une synchronisation a eu lieu.
 */

export function useCustomers(
  storeId: string | undefined,
  query: RequeteClients,
): Page<Customer> | undefined {
  // La requête est sérialisée dans les dépendances : un objet recréé à chaque
  // rendu relancerait la lecture en boucle.
  const key = JSON.stringify(query)
  return useLiveQuery(
    () => (storeId ? searchLocal(storeId, query) : Promise.resolve(undefined)),
    [storeId, key],
  )
}

export function useCities(storeId: string | undefined): string[] {
  return (
    useLiveQuery(() => (storeId ? citiesLocal(storeId) : Promise.resolve([])), [storeId]) ?? []
  )
}

/**
 * Étiquettes réellement employées dans la boutique.
 *
 * ⭐ Proposer l'EXISTANT plutôt qu'une saisie libre : sans cela, « Fidèle »,
 * « fidele » et « Fidéle » cohabitent au bout d'un mois, et le filtre ne
 * ramène plus qu'un tiers des fiches concernées.
 */
export function useEtiquettes(storeId: string | undefined): string[] {
  return (
    useLiveQuery(
      () => (storeId ? etiquettesLocales(storeId) : Promise.resolve([])),
      [storeId],
    ) ?? []
  )
}

/** État du moteur, pour la pastille d'entête (W-04). */
export function useSyncStatus(): SyncStatus {
  const [status, setStatus] = useState<SyncStatus>(currentStatus)
  useEffect(() => subscribe(setStatus), [])
  return status
}

/** Conflits en attente d'arbitrage (W-34). */
export function useConflicts(storeId: string | undefined): ConflictRow[] {
  return (
    useLiveQuery<ConflictRow[]>(
      () => (storeId ? dbFor(storeId).conflicts.toArray() : Promise.resolve([] as ConflictRow[])),
      [storeId],
    ) ?? []
  )
}

/**
 * Une fiche client depuis la réplique.
 *
 * `undefined` = lecture en cours, `null` = absente de la réplique. Distinguer
 * les deux évite d'afficher « introuvable » pendant le chargement.
 */
export function useCustomer(
  storeId: string | undefined,
  id: string | undefined,
): Customer | null | undefined {
  return useLiveQuery(
    () => (storeId && id ? getLocal(storeId, id) : Promise.resolve(null)),
    [storeId, id],
  )
}

// ------------------------------------------------------------------ catalogue

export function useItems(
  storeId: string | undefined,
  query: RequeteArticles,
): Page<Item> | undefined {
  const key = JSON.stringify(query)
  return useLiveQuery(
    () => (storeId ? searchItemsLocal(storeId, query) : Promise.resolve(undefined)),
    [storeId, key],
  )
}

export function useItem(
  storeId: string | undefined,
  id: string | undefined,
): Item | null | undefined {
  return useLiveQuery(
    () => (storeId && id ? getItemLocal(storeId, id) : Promise.resolve(null)),
    [storeId, id],
  )
}

export function useSuppliers(
  storeId: string | undefined,
  query: Partial<SupplierQuery>,
): Page<Supplier> | undefined {
  const key = JSON.stringify(query)
  return useLiveQuery(
    () => (storeId ? searchSuppliersLocal(storeId, query) : Promise.resolve(undefined)),
    [storeId, key],
  )
}

export function useSupplier(
  storeId: string | undefined,
  id: string | undefined,
): Supplier | null | undefined {
  return useLiveQuery(
    () => (storeId && id ? getSupplierLocal(storeId, id) : Promise.resolve(null)),
    [storeId, id],
  )
}

/** Arbre des catégories reconstruit depuis la réplique (plat → hiérarchie). */
export function useCategories(storeId: string | undefined): Category[] {
  return (
    useLiveQuery<Category[]>(async () => {
      if (!storeId) return []
      const db = dbFor(storeId)
      const [rows, items] = await Promise.all([db.categories.toArray(), db.items.toArray()])

      const comptes = new Map<string, number>()
      for (const i of items) {
        const cid = i['category_id'] as string | null
        if (cid) comptes.set(cid, (comptes.get(cid) ?? 0) + 1)
      }

      const parId = new Map(
        rows.map((r) => {
          const c = toCategory(r)
          return [c.id, { ...c, itemCount: comptes.get(c.id) ?? 0, children: [] as Category[] }]
        }),
      )

      const racines: Category[] = []
      for (const c of parId.values()) {
        const parent = c.parentId ? parId.get(c.parentId) : undefined
        if (parent) parent.children.push(c)
        else racines.push(c)
      }
      // Tri par position puis par nom, comme le fait le serveur.
      const trier = (noeuds: Category[]): void => {
        noeuds.sort((a, b) => a.position - b.position || a.name.localeCompare(b.name, 'fr'))
        for (const n of noeuds) trier(n.children)
      }
      trier(racines)
      return racines
    }, [storeId]) ?? []
  )
}

/**
 * Niveaux de stock répliqués, indexés par article.
 *
 * Seuls les AGRÉGATS descendent sur les appareils (§7.5) : la quantité est
 * donc disponible hors ligne, l'historique non.
 */
export function useStockLevels(storeId: string | undefined): Map<string, number> {
  return (
    useLiveQuery<Map<string, number>>(async () => {
      if (!storeId) return new Map()
      const rows = await dbFor(storeId).stockLevels.toArray()
      return new Map(rows.map((r) => [String(r['item_id']), Number(r['quantity'] ?? 0)]))
    }, [storeId]) ?? new Map()
  )
}

/**
 * Ventes de la réplique locale.
 *
 * ⭐ L'historique de caisse se lit hors ligne comme le reste : un commerçant
 * doit pouvoir retrouver le ticket qu'il vient d'émettre, réseau ou pas.
 */
export function useVentes(
  storeId: string | undefined,
  options: RequeteVentes = {},
): Transaction[] | undefined {
  const clef = JSON.stringify(options)
  return useLiveQuery(
    () => (storeId ? listerVentes(storeId, options) : Promise.resolve(undefined)),
    [storeId, clef],
  )
}

export function useVente(
  storeId: string | undefined,
  id: string | undefined,
): Transaction | undefined {
  return useLiveQuery(
    () => (storeId && id ? lireVente(storeId, id) : Promise.resolve(undefined)),
    [storeId, id],
  )
}

/**
 * Référence de chaque document, pour nommer celui dont un autre découle.
 *
 * ⚠️ Lue dans la RÉPLIQUE : le devis d'origine peut dater, mais il y est —
 * l'application réplique tout l'historique de la boutique.
 */
export function useReferencesDocuments(
  storeId: string | undefined,
): Map<string, { reference: string; docType: string }> {
  return (
    useLiveQuery(
      () => (storeId ? referencesParId(storeId) : Promise.resolve(new Map())),
      [storeId],
    ) ?? new Map()
  )
}

export function useTotauxDuJour(
  storeId: string | undefined,
  jour: string,
): { ventes: number; encaisse: number } {
  return (
    useLiveQuery(
      () => (storeId ? totauxDuJour(storeId, jour) : Promise.resolve({ ventes: 0, encaisse: 0 })),
      [storeId, jour],
    ) ?? { ventes: 0, encaisse: 0 }
  )
}

const CHIFFRE_VIDE = {
  montant: 0,
  ventes: 0,
  panierMoyen: 0,
  ventesHt: 0,
  coutAchat: 0,
  lignesSansPrixAchat: 0,
}

export function useChiffreAffaires(
  storeId: string | undefined,
  depuis: string,
  /** Prix d'achat des articles supprimés, fournis par le serveur. */
  complements?: CoutsArticles,
): typeof CHIFFRE_VIDE {
  return (
    useLiveQuery(
      () =>
        storeId ? chiffreAffaires(storeId, depuis, complements) : Promise.resolve(CHIFFRE_VIDE),
      [storeId, depuis, complements],
    ) ?? CHIFFRE_VIDE
  )
}

/** Le tableau de bord entier, recalculé quand la réplique bouge. */
export function useTableauDeBord(
  storeId: string | undefined,
  pas: Pas,
  aujourdhui: string,
  fuseau: string,
  complements?: CoutsArticles,
): Awaited<ReturnType<typeof tableauDeBord>> | undefined {
  return useLiveQuery(
    () =>
      storeId
        ? tableauDeBord(storeId, pas, aujourdhui, fuseau, complements)
        : Promise.resolve(undefined),
    [storeId, pas, aujourdhui, fuseau, complements],
  )
}

/**
 * Les sorties d'argent, tranche par tranche — lues par le RÉSEAU.
 *
 * ⚠️ Les dépenses ne sont pas répliquées. Hors ligne, `null` : les écrans
 * affichent alors un tiret plutôt qu'un bénéfice calculé sans le loyer.
 */
export function useSortiesSerie(
  storeId: string | undefined,
  du: string | undefined,
  au: string,
  pas: Pas,
  actif = true,
): { sorties: SortiesTranche[] | null; enCours: boolean } {
  const [sorties, setSorties] = useState<SortiesTranche[] | null>(null)
  const [enCours, setEnCours] = useState(false)
  useEffect(() => {
    if (!actif || !storeId || !du) return
    let vivant = true
    setEnCours(true)
    void api
      .get<SortiesTranche[]>(`/stores/${storeId}/expenses/serie?du=${du}&au=${au}&pas=${pas}`)
      .then((r) => vivant && setSorties(r))
      .catch(() => vivant && setSorties(null))
      .finally(() => vivant && setEnCours(false))
    return () => {
      vivant = false
    }
  }, [actif, storeId, du, au, pas])
  return { sorties, enCours }
}

const AUCUN_COUT: CoutsArticles = new Map()
/** Au-delà, l'adresse de la requête deviendrait trop longue pour un serveur. */
const IDS_PAR_REQUETE = 100

/**
 * Le prix d'achat des articles que citent les ventes mais que la réplique a
 * perdus — supprimés depuis leur vente.
 *
 * ⭐ Demandé au serveur une fois, et seulement s'il en manque : dans une
 * boutique qui ne supprime rien, aucun appel. Hors ligne, la liste reste vide
 * — le bénéfice net, qui attend lui aussi les dépenses du serveur, n'est
 * alors pas affiché du tout.
 */
export function useCoutsArticlesAbsents(storeId: string | undefined): CoutsArticles {
  const ids = useLiveQuery(
    () => (storeId ? idsArticlesAbsents(storeId) : Promise.resolve([])),
    [storeId],
  )
  const cle = (ids ?? []).join(',')
  const [couts, setCouts] = useState<CoutsArticles>(AUCUN_COUT)
  useEffect(() => {
    if (!storeId || !cle) return
    let vivant = true
    const tous = cle.split(',')
    const lots: string[][] = []
    for (let i = 0; i < tous.length; i += IDS_PAR_REQUETE) lots.push(tous.slice(i, i + IDS_PAR_REQUETE))
    void Promise.all(
      lots.map((lot) =>
        api.get<{ id: string; kind: 'product' | 'service'; purchasePrice: number }[]>(
          `/stores/${storeId}/items/couts?ids=${lot.join(',')}`,
        ),
      ),
    )
      .then((reponses) => {
        if (!vivant) return
        setCouts(
          new Map(
            reponses.flat().map((a) => [a.id, { purchasePrice: a.purchasePrice, kind: a.kind }]),
          ),
        )
      })
      // Hors ligne : les articles restent inconnus, et le bénéfice non affiché.
      .catch(() => undefined)
    return () => {
      vivant = false
    }
  }, [storeId, cle])
  return couts
}

export function useVentesDuClient(
  storeId: string | undefined,
  customerId: string | undefined,
): Transaction[] | undefined {
  return useLiveQuery(
    () => (storeId && customerId ? ventesDuClient(storeId, customerId) : Promise.resolve([])),
    [storeId, customerId],
  )
}

/**
 * Taux de TVA, lus dans la RÉPLIQUE.
 *
 * ⚠️ Pas depuis le serveur : le formulaire d'article et la caisse doivent
 * fonctionner hors ligne, et un taux introuvable y ferait calculer une TVA
 * nulle sans que rien ne le signale.
 */
/** Tous les rendez-vous d'un client, du plus récent au plus ancien. */
export function useRendezVousDuClient(
  storeId: string | undefined,
  customerId: string | undefined,
): RendezVous[] | undefined {
  return useLiveQuery(
    () =>
      storeId && customerId ? rendezVousDuClient(storeId, customerId) : Promise.resolve([]),
    [storeId, customerId],
  )
}

/** Les rendez-vous, rangés par document — pour le journal des ventes. */
export function useRendezVousParVente(
  storeId: string | undefined,
): Map<string, RendezVous> | undefined {
  return useLiveQuery(
    () => (storeId ? rendezVousParVente(storeId) : Promise.resolve(new Map())),
    [storeId],
  )
}

export function useTaxes(storeId: string | undefined): Tax[] {
  return (
    useLiveQuery(async () => {
      if (!storeId) return []
      const lignes = await dbFor(storeId).taxes.toArray()
      return lignes
        .map((r) => {
          const b = r as unknown as Record<string, unknown>
          return {
            id: String(b['id']),
            name: String(b['name'] ?? ''),
            rate: Number(b['rate'] ?? 0),
            isDefault: Boolean(b['is_default']),
            isActive: b['is_active'] !== false,
            version: Number(b['version'] ?? 0),
          }
        })
        .filter((t) => t.isActive)
        .sort((a, b) => b.rate - a.rate)
    }, [storeId]) ?? []
  )
}

/** La série des ventes d'un graphe du tableau de bord, depuis la réplique. */
export function useSerieVentes(
  storeId: string | undefined,
  pas: Pas,
  aujourdhui: string,
  complements?: CoutsArticles,
): Awaited<ReturnType<typeof serieVentes>> | undefined {
  return useLiveQuery(
    () =>
      storeId ? serieVentes(storeId, pas, aujourdhui, complements) : Promise.resolve(undefined),
    [storeId, pas, aujourdhui, complements],
  )
}

/** Le jour de création de chaque fiche client, depuis la réplique. */
export function useJoursCreationClients(storeId: string | undefined): string[] | undefined {
  return useLiveQuery(
    () => (storeId ? joursDeCreationClients(storeId) : Promise.resolve([])),
    [storeId],
  )
}

// ------------------------------------------------------------------- caisse

/**
 * La caisse ouverte, s'il y en a une.
 *
 * ⚠️ `undefined` tant que la réplique n'a pas répondu, `null` quand la réponse
 * est « aucune ». Les confondre ferait clignoter « caisse fermée » au
 * chargement de chaque écran, et refuserait une vente le temps d'un battement.
 */
export function useSessionCaisse(storeId: string | undefined): CashSession | null | undefined {
  return useLiveQuery(
    () => (storeId ? sessionOuverte(storeId) : Promise.resolve(undefined)),
    [storeId],
  )
}

export function useHistoriqueCaisse(storeId: string | undefined): CashSession[] | undefined {
  return useLiveQuery(
    () => (storeId ? historiqueSessions(storeId) : Promise.resolve(undefined)),
    [storeId],
  )
}

export function useMouvementsCaisse(
  storeId: string | undefined,
  sessionId: string | undefined,
): CashMovement[] | undefined {
  return useLiveQuery(
    () => (storeId && sessionId ? mouvementsDeSession(storeId, sessionId) : Promise.resolve([])),
    [storeId, sessionId],
  )
}

/**
 * Les dépenses courantes d'une période, ventilées par catégorie.
 *
 * ⚠️ Lues au SERVEUR, pas dans la réplique : les dépenses ne sont pas
 * répliquées sur les appareils, et le tableau de bord n'est pas un écran de
 * comptoir. Hors ligne, la carte reste donc vide — ce qui vaut mieux qu'un
 * total faux calculé sur ce que l'appareil détient par hasard.
 *
 * ⚠️ L'agrégation est faite en SQL. La regrouper ici aurait impliqué de
 * rapatrier les dépenses, or la liste est plafonnée à 200 lignes : une année
 * de dépenses l'aurait dépassée et le total aurait été tronqué en silence.
 */
export function useDepensesParCategorie(
  storeId: string | undefined,
  du: string | undefined,
  au: string | undefined,
): PartDepense[] | undefined {
  const [parts, setParts] = useState<PartDepense[] | undefined>(undefined)

  useEffect(() => {
    if (!storeId || !du || !au) return
    let vivant = true
    api
      .get<{ data: PartDepense[] }>(
        `/stores/${storeId}/expenses/par-categorie?du=${du}&au=${au}`,
      )
      .then((r) => {
        if (vivant) setParts(r.data)
      })
      .catch(() => {
        if (vivant) setParts([])
      })
    return () => {
      vivant = false
    }
  }, [storeId, du, au])

  return parts
}
